import React, { useRef, useState } from "react";
import { PhoneCall, Square, Loader2, ClipboardCheck } from "lucide-react";
import { RetellWebClient } from "retell-client-js-sdk";
import { Button, StatusBadge, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

type Line = { role: string; content: string };
type Result = { status: string; outcome: string | null; summary: string | null };

export const WebCallCard: React.FC<{ configured: boolean }> = ({ configured }) => {
  const clientRef = useRef<RetellWebClient | null>(null);
  const callRef = useRef<string>("");
  const linesRef = useRef<Line[]>([]);
  const startedRef = useRef<number>(0);
  const [status, setStatus] = useState<"idle" | "connecting" | "live" | "ended">("idle");
  const [lines, setLines] = useState<Line[]>([]);
  const [result, setResult] = useState<Result | null>(null);

  // Finalize using the transcript captured in-browser (does not rely on the provider webhook).
  const finalize = async (id: string) => {
    const dur = startedRef.current ? Math.round((Date.now() - startedRef.current) / 1000) : undefined;
    try {
      const r = await api.voiceCompleteWebCall(id, linesRef.current, dur);
      setResult({ status: r.status, outcome: r.outcome, summary: r.summary });
    } catch (e) {
      toast.error(formatApiError(e));
      // Fallback: try reading a webhook-generated summary.
      await pollResult(id);
    }
  };

  const pollResult = async (id: string, tries = 6) => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { call } = await api.voiceGetCall(id);
        if (call && (call.status === "completed" || call.summary)) {
          setResult({ status: call.status, outcome: call.outcome, summary: call.summary });
          return;
        }
      } catch { /* keep polling */ }
    }
  };

  const start = async () => {
    setStatus("connecting");
    setLines([]);
    linesRef.current = [];
    setResult(null);
    try {
      const { access_token, call_record_id } = await api.voiceRetellWebCall();
      callRef.current = call_record_id;
      const client = new RetellWebClient();
      clientRef.current = client;
      client.on("call_started", () => { startedRef.current = Date.now(); setStatus("live"); });
      client.on("update", (u: any) => { if (u?.transcript) { linesRef.current = u.transcript as Line[]; setLines(u.transcript as Line[]); } });
      client.on("call_ended", () => { setStatus("ended"); if (callRef.current) finalize(callRef.current); });
      client.on("error", (e: any) => { toast.error(String(e?.message || "Call error")); setStatus("ended"); client.stopCall(); if (callRef.current) finalize(callRef.current); });
      await client.startCall({ accessToken: access_token });
    } catch (e) {
      setStatus("idle");
      toast.error(formatApiError(e));
    }
  };

  const stop = () => { clientRef.current?.stopCall(); setStatus("ended"); if (callRef.current) finalize(callRef.current); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="voice-webcall-card">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <PhoneCall className="h-4 w-4 text-blue-600" /> Talk to your AI (web call)
          </h3>
          <p className="text-xs text-slate-400">Live browser call powered by Retell — no phone number needed. Uses your microphone.</p>
        </div>
        {status !== "live"
          ? <Button size="sm" onClick={start} disabled={!configured || status === "connecting"} data-testid="voice-webcall-start">
              {status === "connecting" ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Connecting…</> : <>Start call</>}
            </Button>
          : <Button size="sm" variant="outline" onClick={stop} data-testid="voice-webcall-stop"><Square className="mr-1 h-4 w-4" /> End</Button>}
      </div>

      {!configured && (
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800" data-testid="voice-webcall-not-configured">
          Retell is not connected. Add the provider keys and set the agent's Custom LLM URL to enable live web calls.
        </p>
      )}

      <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-md bg-slate-50 p-3 text-sm" data-testid="voice-webcall-transcript">
        {lines.length === 0
          ? <p className="text-slate-400">Transcript will appear here once the call starts.</p>
          : lines.map((l, i) => (
              <p key={i} className={l.role === "agent" ? "text-blue-700" : "text-slate-700"}>
                <b>{l.role === "agent" ? "AI" : "You"}:</b> {l.content}
              </p>
            ))}
      </div>

      {status === "ended" && (
        <div className="mt-3 rounded-md border border-slate-200 bg-white p-3" data-testid="voice-webcall-result">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <ClipboardCheck className="h-4 w-4 text-emerald-600" /> Call summary
            {result?.outcome && <StatusBadge tone="blue" data-testid="voice-webcall-outcome">{result.outcome.replace(/_/g, " ")}</StatusBadge>}
          </div>
          {result?.summary
            ? <p className="mt-2 text-sm text-slate-700" data-testid="voice-webcall-summary">{result.summary}</p>
            : <p className="mt-2 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Generating outcome &amp; summary…</p>}
        </div>
      )}
    </div>
  );
};
